/**
 * Pure helpers for Fleet Cost Assumptions Qty + number-field edits.
 */

import { resolveCostRateKey, type CostRateBasis } from './costRateBasis';

export interface AssetClassLabel {
  /** Assets "Vehicle Type" (body style / free text). */
  type: string;
  /** Assets "Payload (T)" — typical_tonnes. */
  payloadTonnes: string;
  /** Assets "Acquisition" — absent when not set. */
  acquisition?: 'purchase' | 'lease' | 'rental';
  /** Assets "Deck" (trailers only) — absent when not set. */
  trailer_deck?: 'single' | 'double';
}

/** Vehicles per acquisition method in one rate-table class (from Assets). */
export interface AcquisitionMixFromAssets {
  purchase: number;
  lease: number;
  rental: number;
  /** Acquisition not set on Assets; counted as purchase. */
  unset: number;
}

/** Parse a non-negative number from an input; empty/invalid → 0. */
export function parseNonNegativeNumber(value: string): number {
  const num = parseFloat(value);
  if (Number.isNaN(num) || num < 0) return 0;
  return num;
}

/** Whole-number quantity (assets / divisors); empty/invalid → 0. */
export function parseNonNegativeInteger(value: string): number {
  return Math.floor(parseNonNegativeNumber(value));
}

/**
 * Count Assets roster labels into rate-table names.
 * Tonnage basis prefers Payload (T); body style prefers Vehicle Type.
 */
export function countFleetCostQtyByName(params: {
  assetClassLabels: AssetClassLabel[];
  rateBasis: CostRateBasis;
  isEmergency: boolean;
  rowNames: string[];
}): Record<string, number> {
  const counts: Record<string, number> = {};
  const keyFor = rateTableKeyResolver(params);
  for (const asset of params.assetClassLabels) {
    const key = keyFor(asset);
    if (key == null) continue;
    counts[key] = (counts[key] ?? 0) + 1;
  }
  return counts;
}

/**
 * Count Assets Acquisition per rate-table class, matching vehicles to classes
 * exactly as countFleetCostQtyByName does (so the mix adds up to Quantity).
 */
export function countFleetCostAcquisitionMixByName(params: {
  assetClassLabels: AssetClassLabel[];
  rateBasis: CostRateBasis;
  isEmergency: boolean;
  rowNames: string[];
}): Record<string, AcquisitionMixFromAssets> {
  const mix: Record<string, AcquisitionMixFromAssets> = {};
  const keyFor = rateTableKeyResolver(params);
  for (const asset of params.assetClassLabels) {
    const key = keyFor(asset);
    if (key == null) continue;
    const entry = (mix[key] ??= { purchase: 0, lease: 0, rental: 0, unset: 0 });
    entry[asset.acquisition ?? 'unset'] += 1;
  }
  return mix;
}

/** Rate-table class for one Assets vehicle (null = no emergency row matches). */
function rateTableKeyResolver(params: {
  rateBasis: CostRateBasis;
  isEmergency: boolean;
  rowNames: string[];
}): (asset: AssetClassLabel) => string | null {
  const { rateBasis, isEmergency, rowNames } = params;
  if (isEmergency) {
    const byLower = new Map(rowNames.map((name) => [name.toLowerCase(), name]));
    return (asset) => byLower.get(asset.type.trim().toLowerCase()) ?? null;
  }
  return (asset) => {
    const rawLabel =
      rateBasis === 'tonnage'
        ? (asset.payloadTonnes || asset.type)
        : (asset.type || asset.payloadTonnes);
    return resolveCostRateKey(rawLabel, rateBasis);
  };
}
